import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, DollarSign, Users, Handshake } from 'lucide-react';
interface CaseStudyProofSlideProps {
  content: {
    headline: string;
    subHeadline: string;
    stats: Array<{
      value: string;
      label: string;
      detail: string;
    }>;
    comparisonLabel: string;
    comparisonClient: number;
    comparisonIndustry: number;
    source: string;
  };
}
const statIcons = [TrendingUp, DollarSign, Users, Handshake];
export function CaseStudyProofSlide({
  content
}: CaseStudyProofSlideProps) {
  return <div className="bg-[#1C1C1C] rounded-2xl p-12 min-h-[600px] flex flex-col relative overflow-hidden" data-id="element-66">
      {/* Subtle accent */}
      <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-[#0FB07D] via-[#017C6D] to-[#0FB07D]" data-id="element-67" />

      {/* Header */}
      <div className="text-center mb-10" data-id="element-68">
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="inline-block bg-[#0FB07D]/15 text-[#0FB07D] text-sm font-bold tracking-widest uppercase px-5 py-2 rounded-full mb-5 border border-[#0FB07D]/30" data-id="element-69">
          Case Study: Phoenix Landscaper
        </motion.div>
        <motion.h2 initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="text-4xl md:text-5xl font-bold text-[#F5F5F5] mb-4 leading-tight max-w-4xl mx-auto" data-id="element-70">
          {content.headline}
        </motion.h2>
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.2
      }} className="text-xl text-[#F5F5F5]/60" data-id="element-71">
          {content.subHeadline}
        </motion.p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-5 mb-10" data-id="element-72">
        {content.stats.map((stat, index) => {
        const Icon = statIcons[index];
        return <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3 + index * 0.1
        }} className="bg-[#F5F5F5]/5 border border-[#4D4D4D]/40 rounded-xl p-6 text-center" data-id="element-73">
              <div className="w-12 h-12 bg-[#0FB07D]/15 rounded-lg flex items-center justify-center mx-auto mb-4" data-id="element-74">
                <Icon className="w-6 h-6 text-[#0FB07D]" data-id="element-75" />
              </div>
              <div className="text-4xl font-extrabold text-[#0FB07D] mb-2 tracking-tight" data-id="element-76">
                {stat.value}
              </div>
              <div className="text-lg font-bold text-[#F5F5F5] mb-1" data-id="element-77">
                {stat.label}
              </div>
              <div className="text-sm text-[#F5F5F5]/50" data-id="element-78">{stat.detail}</div>
            </motion.div>;
      })}
      </div>

      {/* Conversion Comparison Bar */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.7
    }} className="bg-[#F5F5F5]/5 border border-[#4D4D4D]/40 rounded-xl p-8" data-id="element-79">
        <h3 className="text-lg font-bold text-[#F5F5F5] mb-6" data-id="element-80">
          {content.comparisonLabel}
        </h3>
        <div className="space-y-5" data-id="element-81">
          {/* Client bar */}
          <div data-id="element-82">
            <div className="flex items-center justify-between mb-2" data-id="element-83">
              <span className="text-sm font-semibold text-[#0FB07D]" data-id="element-84">
                Viridia Client
              </span>
              <span className="text-2xl font-extrabold text-[#0FB07D]" data-id="element-85">
                {content.comparisonClient}%
              </span>
            </div>
            <div className="h-5 bg-[#4D4D4D]/30 rounded-full overflow-hidden" data-id="element-86">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${content.comparisonClient}%`
            }} transition={{
              delay: 0.9,
              duration: 0.8,
              ease: 'easeOut'
            }} className="h-full bg-gradient-to-r from-[#017C6D] to-[#0FB07D] rounded-full" data-id="element-87" />
            </div>
          </div>
          {/* Industry bar */}
          <div data-id="element-88">
            <div className="flex items-center justify-between mb-2" data-id="element-89">
              <span className="text-sm font-semibold text-[#4D4D4D]" data-id="element-90">
                Industry Average
              </span>
              <span className="text-2xl font-extrabold text-[#4D4D4D]" data-id="element-91">
                {content.comparisonIndustry}%
              </span>
            </div>
            <div className="h-5 bg-[#4D4D4D]/30 rounded-full overflow-hidden" data-id="element-92">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${content.comparisonIndustry}%`
            }} transition={{
              delay: 1.0,
              duration: 0.8,
              ease: 'easeOut'
            }} className="h-full bg-[#4D4D4D] rounded-full" data-id="element-93" />
            </div>
          </div>
        </div>
      </motion.div>

      <div className="absolute bottom-5 right-8 text-sm text-[#4D4D4D] font-medium" data-id="element-94">
        {content.source}
      </div>
    </div>;
}
export default CaseStudyProofSlide;